// Minimal typing — showSaveFilePicker isn't in the default TS DOM lib yet.
type SaveFilePicker = (opts?: {
  suggestedName?: string;
  types?: { description: string; accept: Record<string, string[]> }[];
}) => Promise<FileSystemFileHandle>;

type StreamZipToDiskArgs = {
  /** Same-origin endpoint that returns the ZIP bytes. */
  url: string;
  /** Filename offered in the "Save As" dialog (`.zip` appended if missing). */
  suggestedName: string;
  /** Bearer token for the API call (Redux `state.user.loginToken`). */
  token: string | null;
  method?: 'GET' | 'POST';
  /** JSON request body (POST only); omitted for GET. */
  body?: unknown;
};

/**
 * Streams a bulk ZIP straight to disk via the File System Access API, so the
 * archive is never buffered in memory. The old `response.blob()` path holds the
 * whole archive in the renderer (~900 MB for two heavy files) and OOM-crashes
 * the tab ("Aw, Snap!") on large archives (services 0.27.2, #1615).
 *
 * Returns `false` when the browser can't stream (no `showSaveFilePicker` —
 * Firefox/Safari, or a non-secure context), so the caller can fall back to the
 * buffered blob path. Throws on fetch errors and on the user cancelling the
 * save dialog (`AbortError`) — callers treat `AbortError` as a no-op.
 *
 * MUST be invoked directly inside a click handler, before any earlier `await`,
 * so `showSaveFilePicker` runs within the user-activation window and the
 * browser doesn't block it.
 */
export async function streamZipToDisk({
  url,
  suggestedName,
  token,
  method = 'GET',
  body,
}: StreamZipToDiskArgs): Promise<boolean> {
  const showSaveFilePicker = (
    window as unknown as { showSaveFilePicker?: SaveFilePicker }
  ).showSaveFilePicker;

  // Chromium-only; also requires a secure context (HTTPS/localhost).
  if (typeof showSaveFilePicker !== 'function') return false;

  // Neutralize path separators and characters that are invalid in filenames on
  // common OSes, so a folder/project label like "FY25 / Q1" doesn't produce an
  // invalid suggestedName that makes showSaveFilePicker throw (which would
  // otherwise surface as a spurious download failure). Spaces and hyphens are
  // intentionally preserved.
  const cleaned = suggestedName.replace(/[/\\:*?"<>|]/g, '_').trim() || 'download';
  const name = /\.zip$/i.test(cleaned) ? cleaned : `${cleaned}.zip`;

  // Call the picker FIRST, before any await, so it runs inside the click's
  // user-activation window and isn't blocked by the browser.
  const handle = await showSaveFilePicker({
    suggestedName: name,
    types: [
      { description: 'ZIP archive', accept: { 'application/zip': ['.zip'] } },
    ],
  });

  const response = await fetch(url, {
    method,
    credentials: 'include',
    headers: {
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });

  if (!response.ok || !response.body) {
    throw new Error(`Download failed with status ${response.status}`);
  }

  // pipeTo streams chunk-by-chunk to disk and closes the file on completion —
  // peak memory is one chunk, never the whole archive.
  const writable = await handle.createWritable();
  await response.body.pipeTo(writable);
  return true;
}
